import React, { useEffect, useState } from 'react'
import cn from 'classnames'
import { Modal } from 'antd'
import { pluralN } from './genUtils'
import { QuestionBody, TYPE_LABEL, TYPE_ORDER, TYPE_TITLE } from './TestQuestion'
import s from './gen.module.scss'
import t from './testgen.module.scss'

const RefreshIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M20 11a8 8 0 1 0-2.3 5.7" />
    <path d="M20 4v7h-7" />
  </svg>
)
const TrashIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    aria-hidden="true"
  >
    <path d="M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3" />
  </svg>
)

// Окно «Проверьте вопросы перед созданием»: вопросы по темам, заменить / убрать / добавить - бесплатно.
const TestReview = ({
  open,
  draft,
  title,
  number,
  creating,
  readOnly,
  onClose,
  onReplace,
  onRemove,
  onAdd,
  onCreate,
}) => {
  const topics = draft?.topics || []
  const [topic, setTopic] = useState(topics[0]?.name)
  const [addType, setAddType] = useState('single')
  useEffect(() => {
    if (!topics.some((x) => x.name === topic)) setTopic(topics[0]?.name)
  }, [draft?.id, topics.length])
  if (!draft) return null
  const questions = draft.questions || []
  const numbered = questions.filter((q) => q.state !== 'adding')
  const inTopic = questions.filter((q) => q.topic === topic)
  const busy = questions.some((q) => q.state)
  const countOf = (name) => questions.filter((q) => q.topic === name && q.state !== 'adding').length
  const usedTypes = TYPE_ORDER.filter((k) => draft.types?.includes(k))
  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={1080}
      className={t.modal}
      centered
      destroyOnClose
    >
      <div className={s.vars}>
        <div className={t.rv} role="document">
          <div className={t.rvHead}>
            <div>
              <div className={s.kicker}>
                {title} · Вариант {number} · {readOnly ? 'тест создан' : 'ещё не создан'}
              </div>
              <h2>{readOnly ? 'Вопросы теста' : 'Проверьте вопросы перед созданием'}</h2>
              <div className={cn(s.muted, s.small)}>
                {readOnly
                  ? 'Править созданный тест — в редакторе курса'
                  : 'Неудачный вопрос можно заменить или убрать, а недостающий — добавить. Это бесплатно.'}
              </div>
            </div>
            <button type="button" className={cn(s.btn, s.ghost, s.icon)} aria-label="Закрыть" onClick={onClose}>
              ×
            </button>
          </div>
          <div className={t.rvBody}>
            <nav className={t.rvNav} aria-label="Темы">
              <div className={cn(s.muted, s.small)} style={{ padding: '0 12px 6px', fontWeight: 600 }}>
                Темы · {pluralN(numbered.length, ['вопрос', 'вопроса', 'вопросов'])}
              </div>
              {topics.map((tp) => (
                <button
                  type="button"
                  key={tp.name}
                  aria-pressed={topic === tp.name}
                  className={cn(t.toc, { [t.tocOn]: topic === tp.name })}
                  onClick={() => setTopic(tp.name)}
                >
                  <span>{tp.name}</span>
                  <span className={s.muted}>{countOf(tp.name)}</span>
                </button>
              ))}
            </nav>
            <div className={t.rvList}>
              <div style={{ fontWeight: 600 }}>{topic}</div>
              {inTopic.map((q) => {
                const n = numbered.indexOf(q) + 1
                const state = q.state === 'replacing' ? 'заменяется…' : q.state === 'adding' ? 'генерируется…' : ''
                return (
                  <article key={q.id} className={cn(t.q, { [t.qBusy]: !!q.state })} aria-busy={!!q.state}>
                    <div className={t.qHead}>
                      <div className={t.qMeta}>
                        {n > 0 && <span>Вопрос {n}</span>}
                        <span className={cn(t.tag, t[`tc_${q.type}`])}>{TYPE_LABEL[q.type]}</span>
                        {state && <span className={t.tag}>{state}</span>}
                        {q.failed && !state && <span className={t.tag}>не удалось заменить</span>}
                        {q.source?.page && <span>с. {q.source.page}</span>}
                      </div>
                      {!readOnly && !q.state && (
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button type="button" className={cn(s.btn, s.sec, s.sm)} onClick={() => onReplace(q)}>
                            <RefreshIcon />
                            Заменить
                          </button>
                          <button
                            type="button"
                            className={cn(s.btn, s.ghost, s.sm)}
                            aria-label={`Убрать вопрос ${n}`}
                            disabled={numbered.length <= 1}
                            onClick={() => onRemove(q)}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      )}
                    </div>
                    {q.state === 'adding' ? (
                      <div className={cn(s.muted, s.small)}>ИИ составляет вопрос по теме «{q.topic}»…</div>
                    ) : (
                      <>
                        <div className={t.qText}>{q.question}</div>
                        <QuestionBody q={q} />
                        {q.explanation && <div className={t.expl}>Почему так: {q.explanation}</div>}
                        {q.source?.quote && <blockquote className={t.quote}>«{q.source.quote}»</blockquote>}
                      </>
                    )}
                  </article>
                )
              })}
              {!readOnly && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    className={cn(s.btn, s.sec)}
                    style={{ borderStyle: 'dashed', flex: '1 1 260px' }}
                    onClick={() => onAdd(topic, addType)}
                  >
                    + Добавить вопрос по теме «{topic}»
                  </button>
                  {usedTypes.length > 1 && (
                    <div className={s.seg} role="radiogroup" aria-label="Тип нового вопроса">
                      {usedTypes.map((k) => (
                        <button
                          type="button"
                          key={k}
                          role="radio"
                          aria-checked={addType === k}
                          className={cn({ [s.segOn]: addType === k })}
                          onClick={() => setAddType(k)}
                        >
                          {TYPE_TITLE[k]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className={t.rvFoot}>
            <div className={cn(s.muted, s.small)}>
              {pluralN(numbered.length, ['вопрос', 'вопроса', 'вопросов'])} · ~{draft.minutes} мин
              {busy ? ' · идёт замена вопросов' : ''}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              <button type="button" className={cn(s.btn, s.sec)} onClick={onClose}>
                {readOnly ? 'Закрыть' : 'Назад к вариантам'}
              </button>
              {!readOnly && (
                <button type="button" className={cn(s.btn, s.pri)} disabled={busy || creating} onClick={onCreate}>
                  {creating ? 'Создаём тест…' : 'Создать тест · 1 генерация'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default TestReview
